import { cnlPath } from "csi/typings";

const _rePath = /^(Child|Parent|Index):/;

export const PARENT_PATH = "Child:__Parent__";
export const PATH_OP_CHILD = "Child";
export const PATH_OP_INDEX = "Index";
export const PATH_OP_PARENT = "Parent";
export const PATH_SEPARATOR = "/";
export const PATH_STEP_CONJUNCTION = ":";
export const URL_PATH = "?Path=";

/**
 * Extend the given cnl-path pair with the given path
 */
export const extendUrlPath = (urlPath: cnlPath, additionalPath?: string): cnlPath => {
	if (!additionalPath) {
		return urlPath;
	}

	const [url, path] = urlPath;
	return [url, pathCreate(path, additionalPath)];
};

/**
 * Determine if given string is a path operator
 */
export const isPathOperator = (op: string): boolean => (op === PATH_OP_CHILD) || (op === PATH_OP_INDEX) || (op === PATH_OP_PARENT);

/**
 * Compare two paths, either of which could be undefined/empty
 */
export const pathsAreSame = (path1: string, path2: string): boolean => (path1 || "") === (path2 || "");

/**
 * Constructing URL with Path
 */
export const getUrlPath = (url: string, path: string, bForClient?: boolean): string => {
	if (path) {
		const pathPrefix = isUrlWithPath(url) ? PATH_SEPARATOR : URL_PATH;
		const pathToAdd = bForClient ? path : escape(path);
		return `${url}${pathPrefix}${pathToAdd}`;
	}
	return url;
};

/**
 * Checking if Url contains path
 */
export const isUrlWithPath = (url: string): boolean => {
	return url?.indexOf(URL_PATH) > 0;
};

/**
 * Construct path from given arguments which can be mix of operators (Child,Parent,Index) and
 * relationship ids / indexes. If no operator given, then Child is assumed.
 */
export const pathCreate = (...args: string[]): string => {
	let op: string;
	let p = "";

	for (const s of args) {
		if (isPathOperator(s)) {
			op = s;
		} else if (s || ((s != null) && (op === PATH_OP_INDEX))) {
			if (p) {
				p += PATH_SEPARATOR;
			}

			if (_rePath.test(s)) {
				p += s;
			} else {
				p += (op || PATH_OP_CHILD) + PATH_STEP_CONJUNCTION + s;
			}

			op = null;
		}
	}

	return p;
};

/**
 * Remove last step in path, returning the remaining path
 */
export const pathRemoveStep = (path: string): string | undefined => {
	const n = path?.lastIndexOf(PATH_SEPARATOR) ?? -1;
	return (n > 0) ? path.substring(0, n) : undefined;
};

/**
 * Split path into array of steps
 */
export const pathGetSteps = (path: string): string[] | undefined => path ? path.split(PATH_SEPARATOR) : undefined;

/**
 * Return path operator for given path step
 */
export const pathGetStepOperator = (pathStep: string): string | undefined => pathStep ? pathStep.split(PATH_STEP_CONJUNCTION)[0] : undefined;

/**
 * Return operand of given path step
 */
export const pathGetStepOperand = (pathStep: string): string | undefined => {
	const a = pathStep?.split(PATH_STEP_CONJUNCTION);
	return (a?.length === 2) ? a[1] : undefined;
};

/**
 * Return true if given path contains a parent step
 */
export const pathHasParentStep = (path: string): boolean => path?.indexOf(PATH_OP_PARENT + PATH_STEP_CONJUNCTION) >= 0;